import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Tab, Tabs, Badge, Collapse, Button, ButtonGroup, Modal, Form, InputGroup, Alert } from 'react-bootstrap';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import FinancialChart from './FinancialChart';
import StockPerformanceChart from './StockPerformanceChart';
import CountryAllocationChart from './CountryAllocationChart';
import { toast } from 'react-toastify';
import { CACHE_CONFIG, STOCK_DETAIL_CONFIG } from '../../shared/services/config';
import { notifyTradeExecution } from '../../shared/utils/tradeNotifications';
import { formatToLocalTime } from '../../shared/utils/dateUtils';

const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

const formatCompact = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
};

const DATE_TIME_FORMAT_OPTIONS = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
};

const getFirstFieldValue = (obj, keys) => {
    if (!obj) return null;
    for (const key of keys) {
        const value = obj[key];
        if (value === null || value === undefined) continue;
        if (typeof value === 'number') return value;
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }
        return value;
    }
    return null;
};

const fetchWithErrorHandling = async (url, headers) => {
    try {
        const response = await fetch(url, { headers });
        if (!response.ok) {
            console.warn(`API endpoint not available: ${url}`);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.warn(`Failed to fetch ${url}:`, error.message);
        return null;
    }
};

const getCache = (key, expiryMinutes = CACHE_CONFIG.localStorageDefaultMinutes) => {
    const cached = localStorage.getItem(key);
    if (cached) {
        const { value, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();
        if (now - timestamp < expiryMinutes * 60 * 1000) {
            return value;
        } else {
            localStorage.removeItem(key);
        }
    }
    return null;
};

const setCache = (key, value) => {
    const now = new Date().getTime();
    const item = { value, timestamp: now };
    const serialized = JSON.stringify(item);
    try {
        localStorage.setItem(key, serialized);
    } catch (error) {
        // Handle storage quota exceeded: evict oldest cached items and retry once
        try {
            const candidates = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (!k) continue;
                if (!k.startsWith('fmp_') && !k.startsWith('sec_')) continue;
                try {
                    const raw = localStorage.getItem(k);
                    if (!raw) continue;
                    const parsed = JSON.parse(raw);
                    if (parsed && typeof parsed.timestamp === 'number') {
                        candidates.push({ key: k, timestamp: parsed.timestamp });
                    }
                } catch {
                    // Ignore malformed entries
                }
            }
            candidates.sort((a, b) => a.timestamp - b.timestamp);
            // Remove up to 10 oldest cached entries
            for (let i = 0; i < Math.min(10, candidates.length); i++) {
                localStorage.removeItem(candidates[i].key);
            }
            localStorage.setItem(key, serialized);
        } catch {
            // Give up silently if storage is still full or inaccessible
        }
    }
};

const calculateSMA = (data, period) => {
    if (!data || data.length < period) return [];
    const sma = [];
    for (let i = 0; i < data.length; i++) {
        if (i < period - 1) {
            sma.push(null);
        } else {
            const sum = data.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val, 0);
            sma.push(sum / period);
        }
    }
    return sma;
};

const calculateRSI = (data, period = 14) => {
    if (!data || data.length <= period) return [];

    const rsi = Array(data.length).fill(null);
    let gains = 0;
    let losses = 0;

    // Calculate initial average gain and loss
    for (let i = 1; i <= period; i++) {
        const change = data[i].close - data[i-1].close;
        if (change > 0) {
            gains += change;
        } else {
            losses -= change;
        }
    }

    let avgGain = gains / period;
    let avgLoss = losses / period;

    // Calculate first RSI
    if (avgLoss === 0) {
        rsi[period] = 100;
    } else {
        const rs = avgGain / avgLoss;
        rsi[period] = 100 - (100 / (1 + rs));
    }

    // Calculate subsequent RSI values
    for (let i = period + 1; i < data.length; i++) {
        const change = data[i].close - data[i-1].close;
        let currentGain = 0;
        let currentLoss = 0;

        if (change > 0) {
            currentGain = change;
        } else {
            currentLoss = -change;
        }

        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;

        if (avgLoss === 0) {
            rsi[i] = 100;
        } else {
            const rs = avgGain / avgLoss;
            rsi[i] = 100 - (100 / (1 + rs));
        }
    }
    return rsi;
};

const calculateDailyPercentageChange = (data) => {
    if (!data || data.length < 2) return [];
    const changes = [null]; // First day has no previous day for change

    for (let i = 1; i < data.length; i++) {
        const currentPrice = data[i].close;
        const previousPrice = data[i-1].close;
        if (previousPrice !== 0) {
            changes.push(((currentPrice - previousPrice) / previousPrice) * 100);
        } else {
            changes.push(null); // Avoid division by zero
        }
    }
    return changes;
};

// Investment Analysis Functions
const calculateVolatility = (data, period = 20) => {
    if (!data || data.length < period) return null;
    const returns = [];
    for (let i = 1; i < data.length; i++) {
        const change = (data[i].close - data[i-1].close) / data[i-1].close;
        returns.push(change);
    }
    const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / returns.length;
    return Math.sqrt(variance * 252) * 100; // Annualized volatility
};

const calculateSharpeRatio = (data, riskFreeRate = 0.02) => {
    if (!data || data.length < 2) return null;
    const returns = [];
    for (let i = 1; i < data.length; i++) {
        returns.push((data[i].close - data[i-1].close) / data[i-1].close);
    }
    const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length * 252;
    const volatility = calculateVolatility(data) / 100;
    return volatility ? ((avgReturn - riskFreeRate) / volatility).toFixed(2) : null;
};

const calculateSupport = (data, period = 20) => {
    if (!data || data.length < period) return null;
    const recent = data.slice(-period);
    const lows = recent
        .map(d => (d.adjLow ?? d.low))
        .filter(v => typeof v === 'number' && !Number.isNaN(v));
    if (lows.length === 0) return null;
    return Math.min(...lows);
};

const calculateResistance = (data, period = 20) => {
    if (!data || data.length < period) return null;
    const recent = data.slice(-period);
    const highs = recent
        .map(d => (d.adjHigh ?? d.high))
        .filter(v => typeof v === 'number' && !Number.isNaN(v));
    if (highs.length === 0) return null;
    return Math.max(...highs);
};

const calculateBollingerBands = (data, period = 20, stdDev = 2) => {
    if (!data || data.length < period) return { upper: [], middle: [], lower: [] };
    
    const closes = data.map(d => d.close);
    const sma = calculateSMA(closes, period);
    const upper = [];
    const lower = [];
    
    for (let i = 0; i < data.length; i++) {
        if (i < period - 1) {
            upper.push(null);
            lower.push(null);
        } else {
            const slice = closes.slice(i - period + 1, i + 1);
            const mean = slice.reduce((sum, val) => sum + val, 0) / period;
            const variance = slice.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / period;
            const std = Math.sqrt(variance);
            upper.push(mean + (stdDev * std));
            lower.push(mean - (stdDev * std));
        }
    }
    
    return { upper, middle: sma, lower };
};

const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
    if (!data || data.length < slowPeriod) return { macd: [], signal: [], histogram: [] };
    
    const closes = data.map(d => d.close);
    const emaFast = calculateEMA(closes, fastPeriod);
    const emaSlow = calculateEMA(closes, slowPeriod);
    
    const macd = [];
    for (let i = 0; i < closes.length; i++) {
        if (emaFast[i] !== null && emaSlow[i] !== null) {
            macd.push(emaFast[i] - emaSlow[i]);
        } else {
            macd.push(null);
        }
    }
    
    const signal = calculateEMA(macd.filter(v => v !== null), signalPeriod);
    const histogram = [];
    
    for (let i = 0; i < macd.length; i++) {
        if (macd[i] !== null && signal[i - (slowPeriod - 1)] !== null) {
            histogram.push(macd[i] - signal[i - (slowPeriod - 1)]);
        } else {
            histogram.push(null);
        }
    }
    
    return { macd, signal, histogram };
};

const calculateEMA = (data, period) => {
    if (!data || data.length < period) return [];
    
    const ema = [];
    const multiplier = 2 / (period + 1);
    
    // Start with SMA for first value
    let sum = 0;
    for (let i = 0; i < period; i++) {
        if (data[i] !== null) sum += data[i];
        ema.push(null);
    }
    ema[period - 1] = sum / period;
    
    // Calculate EMA for remaining values
    for (let i = period; i < data.length; i++) {
        if (data[i] !== null && ema[i - 1] !== null) {
            ema.push((data[i] * multiplier) + (ema[i - 1] * (1 - multiplier)));
        } else {
            ema.push(null);
        }
    }
    
    return ema;
};

const calculateStochastic = (data, kPeriod = 14, dPeriod = 3) => {
    if (!data || data.length < kPeriod) return { k: [], d: [] };
    
    const k = [];
    
    for (let i = 0; i < data.length; i++) {
        if (i < kPeriod - 1) {
            k.push(null);
        } else {
            const slice = data.slice(i - kPeriod + 1, i + 1);
            const highs = slice
                .map(d => (d.adjHigh ?? d.high))
                .filter(v => typeof v === 'number' && !Number.isNaN(v));
            const lows = slice
                .map(d => (d.adjLow ?? d.low))
                .filter(v => typeof v === 'number' && !Number.isNaN(v));
            if (highs.length === 0 || lows.length === 0) {
                k.push(null);
                continue;
            }
            const highest = Math.max(...highs);
            const lowest = Math.min(...lows);
            const current = data[i].close;
            
            if (highest === lowest) {
                k.push(50);
            } else {
                k.push(((current - lowest) / (highest - lowest)) * 100);
            }
        }
    }
    
    const d = [];
    for (let i = 0; i < k.length; i++) {
        if (i < kPeriod - 1 + dPeriod - 1) {
            d.push(null);
            continue;
        }
        const window = k.slice(i - dPeriod + 1, i + 1);
        if (window.some(v => v === null || Number.isNaN(v))) {
            d.push(null);
            continue;
        }
        const avg = window.reduce((sum, v) => sum + v, 0) / dPeriod;
        d.push(avg);
    }
    
    return { k, d };
};

const calculateATRSeries = (data, period = 14) => {
    if (!data || data.length < period + 1) return [];
    const trList = [null];
    for (let i = 1; i < data.length; i++) {
        const high = data[i].adjHigh ?? data[i].high;
        const low = data[i].adjLow ?? data[i].low;
        const prevClose = data[i - 1].close;
        if (![high, low, prevClose].every(v => typeof v === 'number' && !Number.isNaN(v))) {
            trList.push(null);
            continue;
        }
        const tr = Math.max(
            high - low,
            Math.abs(high - prevClose),
            Math.abs(low - prevClose)
        );
        trList.push(tr);
    }
    const atr = Array(data.length).fill(null);
    for (let i = period; i < trList.length; i++) {
        const window = trList.slice(i - period + 1, i + 1);
        if (window.some(v => v === null || Number.isNaN(v))) {
            atr[i] = null;
            continue;
        }
        atr[i] = window.reduce((sum, v) => sum + v, 0) / period;
    }
    return atr;
};

const calculateADXSeries = (data, period = 14) => {
    if (!data || data.length < period + 1) return [];
    const plusDM = [null];
    const minusDM = [null];
    const trList = [null];

    for (let i = 1; i < data.length; i++) {
        const high = data[i].adjHigh ?? data[i].high;
        const low = data[i].adjLow ?? data[i].low;
        const prevHigh = data[i - 1].adjHigh ?? data[i - 1].high;
        const prevLow = data[i - 1].adjLow ?? data[i - 1].low;
        const prevClose = data[i - 1].close;

        if (![high, low, prevHigh, prevLow, prevClose].every(v => typeof v === 'number' && !Number.isNaN(v))) {
            plusDM.push(null);
            minusDM.push(null);
            trList.push(null);
            continue;
        }

        const upMove = high - prevHigh;
        const downMove = prevLow - low;
        plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
        minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
        const tr = Math.max(
            high - low,
            Math.abs(high - prevClose),
            Math.abs(low - prevClose)
        );
        trList.push(tr);
    }

    const adx = Array(data.length).fill(null);
    for (let i = period; i < data.length; i++) {
        const trWindow = trList.slice(i - period + 1, i + 1);
        const plusWindow = plusDM.slice(i - period + 1, i + 1);
        const minusWindow = minusDM.slice(i - period + 1, i + 1);
        if (trWindow.some(v => v === null) || plusWindow.some(v => v === null) || minusWindow.some(v => v === null)) {
            adx[i] = null;
            continue;
        }
        const trSum = trWindow.reduce((sum, v) => sum + v, 0);
        if (trSum === 0) {
            adx[i] = null;
            continue;
        }
        const plusDI = 100 * (plusWindow.reduce((sum, v) => sum + v, 0) / trSum);
        const minusDI = 100 * (minusWindow.reduce((sum, v) => sum + v, 0) / trSum);
        const dx = 100 * (Math.abs(plusDI - minusDI) / (plusDI + minusDI));
        adx[i] = Number.isFinite(dx) ? dx : null;
    }
    return adx;
};

const calculateOBVSeries = (data) => {
    if (!data || data.length < 2) return [];
    const obv = Array(data.length).fill(null);
    let running = 0;
    for (let i = 1; i < data.length; i++) {
        const prevClose = data[i - 1].close;
        const close = data[i].close;
        const volume = data[i].volume;
        if (![prevClose, close, volume].every(v => typeof v === 'number' && !Number.isNaN(v))) {
            obv[i] = null;
            continue;
        }
        if (close > prevClose) running += volume;
        else if (close < prevClose) running -= volume;
        obv[i] = running;
    }
    return obv;
};

const getInvestmentSignals = (data, currentPrice, rsi, sma50, sma200, bollingerBands, macd, stochastic, atrSeries, adxSeries, obvSeries) => {
    if (!data || data.length < 50) return [];
    
    const signals = [];
    const latestRSI = rsi[rsi.length - 1];
    const latest50SMA = sma50[sma50.length - 1];
    const latest200SMA = sma200[sma200.length - 1];
    const latestMACD = macd.macd[macd.macd.length - 1];
    const latestSignal = macd.signal[macd.signal.length - 1];
    const prevMACD = macd.macd[macd.macd.length - 2];
    const prevSignal = macd.signal[macd.signal.length - 2];
    const latestStochK = stochastic.k[stochastic.k.length - 1];
    const latestBBUpper = bollingerBands.upper[bollingerBands.upper.length - 1];
    const latestBBLower = bollingerBands.lower[bollingerBands.lower.length - 1];
    const hasNumber = (val) => typeof val === 'number' && !Number.isNaN(val);
    const pushNA = (signal, description) => signals.push({ signal, strength: 'N/A', description, color: 'secondary' });
    
    // RSI Signal
    if (!hasNumber(latestRSI)) {
        pushNA('RSI', 'Insufficient RSI data');
    } else if (latestRSI < 30) {
        signals.push({ signal: 'RSI', strength: 'BUY', description: `RSI at ${latestRSI.toFixed(1)} suggests oversold`, color: 'success' });
    } else if (latestRSI > 70) {
        signals.push({ signal: 'RSI', strength: 'SELL', description: `RSI at ${latestRSI.toFixed(1)} suggests overbought`, color: 'danger' });
    } else {
        signals.push({ signal: 'RSI', strength: 'HOLD', description: `RSI at ${latestRSI.toFixed(1)} is neutral`, color: 'warning' });
    }
    
    // Moving Average Trend
    if (!hasNumber(currentPrice) || !hasNumber(latest50SMA) || !hasNumber(latest200SMA)) {
        pushNA('Trend (SMA)', 'Insufficient SMA data');
    } else if (currentPrice > latest50SMA && latest50SMA > latest200SMA) {
        signals.push({ signal: 'Trend (SMA)', strength: 'BUY', description: 'Price above SMA50 & SMA200 (bullish)', color: 'success' });
    } else if (currentPrice < latest50SMA && latest50SMA < latest200SMA) {
        signals.push({ signal: 'Trend (SMA)', strength: 'SELL', description: 'Price below SMA50 & SMA200 (bearish)', color: 'danger' });
    } else {
        signals.push({ signal: 'Trend (SMA)', strength: 'HOLD', description: 'Mixed SMA positioning', color: 'warning' });
    }
    
    // MACD Signal
    if (!hasNumber(latestMACD) || !hasNumber(latestSignal) || !hasNumber(prevMACD) || !hasNumber(prevSignal)) {
        pushNA('MACD', 'Insufficient MACD data');
    } else if (latestMACD > latestSignal && prevMACD <= prevSignal) {
        signals.push({ signal: 'MACD', strength: 'BUY', description: 'MACD crossed above signal line', color: 'success' });
    } else if (latestMACD < latestSignal && prevMACD >= prevSignal) {
        signals.push({ signal: 'MACD', strength: 'SELL', description: 'MACD crossed below signal line', color: 'danger' });
    } else {
        signals.push({ signal: 'MACD', strength: 'HOLD', description: 'No recent cross', color: 'warning' });
    }
    
    // Bollinger Bands Signal
    if (!hasNumber(currentPrice) || !hasNumber(latestBBLower) || !hasNumber(latestBBUpper)) {
        pushNA('Bollinger Bands', 'Insufficient Bollinger data');
    } else if (currentPrice <= latestBBLower) {
        signals.push({ signal: 'Bollinger Bands', strength: 'BUY', description: 'Price near lower band (oversold)', color: 'success' });
    } else if (currentPrice >= latestBBUpper) {
        signals.push({ signal: 'Bollinger Bands', strength: 'SELL', description: 'Price near upper band (overbought)', color: 'danger' });
    } else {
        signals.push({ signal: 'Bollinger Bands', strength: 'HOLD', description: 'Price within bands', color: 'warning' });
    }
    
    // Stochastic Signal
    if (!hasNumber(latestStochK)) {
        pushNA('Stochastic', 'Insufficient stochastic data');
    } else if (latestStochK < 20) {
        signals.push({ signal: 'Stochastic', strength: 'BUY', description: `Stoch at ${latestStochK.toFixed(1)} (oversold)`, color: 'success' });
    } else if (latestStochK > 80) {
        signals.push({ signal: 'Stochastic', strength: 'SELL', description: `Stoch at ${latestStochK.toFixed(1)} (overbought)`, color: 'danger' });
    } else {
        signals.push({ signal: 'Stochastic', strength: 'HOLD', description: `Stoch at ${latestStochK.toFixed(1)} is neutral`, color: 'warning' });
    }
    
    // Volume Analysis
    if (data.length < 20) {
        pushNA('Volume', 'Insufficient volume data');
    } else {
        const avgVolume = data.slice(-20).reduce((sum, d) => sum + d.volume, 0) / 20;
        const latestVolume = data[data.length - 1].volume;
        if (latestVolume > avgVolume * 1.5) {
            signals.push({ signal: 'Volume', strength: 'WATCH', description: 'Volume 50% above average', color: 'warning' });
        } else {
            signals.push({ signal: 'Volume', strength: 'HOLD', description: 'Volume near average', color: 'warning' });
        }
    }
    
    // Price Momentum
    if (!hasNumber(currentPrice) || !data[data.length - 6]?.close) {
        pushNA('Momentum (5D)', 'Insufficient 5-day data');
    } else {
        const priceChange5d = ((currentPrice - data[data.length - 6].close) / data[data.length - 6].close) * 100;
        if (priceChange5d > 5) {
            signals.push({ signal: 'Momentum (5D)', strength: 'BUY', description: '5-day gain > 5%', color: 'success' });
        } else if (priceChange5d < -5) {
            signals.push({ signal: 'Momentum (5D)', strength: 'SELL', description: '5-day loss > 5%', color: 'danger' });
        } else {
            signals.push({ signal: 'Momentum (5D)', strength: 'HOLD', description: '5-day change within ±5%', color: 'warning' });
        }
    }
    
    // Volatility Spike
    const volatility = calculateVolatility(data.slice(-20));
    const avgVolatility = calculateVolatility(data.slice(-60, -20));
    if (!hasNumber(volatility) || !hasNumber(avgVolatility)) {
        pushNA('Volatility', 'Insufficient volatility data');
    } else if (volatility > avgVolatility * 1.3) {
        signals.push({ signal: 'Volatility', strength: 'WATCH', description: 'Volatility 30% above average', color: 'warning' });
    } else {
        signals.push({ signal: 'Volatility', strength: 'HOLD', description: 'Volatility near average', color: 'warning' });
    }

    // ATR Signal (volatility expansion)
    const atr = atrSeries?.[atrSeries.length - 1];
    if (!hasNumber(atr)) {
        pushNA('ATR', 'Insufficient ATR data');
    } else {
        signals.push({ signal: 'ATR', strength: 'HOLD', description: `ATR (14): ${atr.toFixed(2)}`, color: 'warning' });
    }

    // ADX Signal (trend strength)
    const adx = adxSeries?.[adxSeries.length - 1];
    if (!hasNumber(adx)) {
        pushNA('ADX', 'Insufficient ADX data');
    } else if (adx >= 25) {
        signals.push({ signal: 'ADX', strength: 'WATCH', description: `Strong trend (ADX ${adx.toFixed(1)})`, color: 'warning' });
    } else {
        signals.push({ signal: 'ADX', strength: 'HOLD', description: `Weak trend (ADX ${adx.toFixed(1)})`, color: 'warning' });
    }

    // OBV Signal (volume flow)
    const obv = obvSeries?.[obvSeries.length - 1];
    if (!hasNumber(obv)) {
        pushNA('OBV', 'Insufficient OBV data');
    } else {
        signals.push({ signal: 'OBV', strength: 'HOLD', description: `OBV: ${formatCompact(obv)}`, color: 'warning' });
    }
    
    return signals;
};

const trimHistoricalData = (data, timeWindow, timeDuration) => {
    if (!data || data.length === 0 || timeDuration === 'alltime') {
        return data;
    }

    const pointsPerInterval = {
        '5min': 78,    // Approx 6.5 hours * 60 min / 5 min
        '15min': 26,   // Approx 6.5 hours * 60 min / 15 min
        '30min': 13,   // Approx 6.5 hours * 60 min / 30 min
        '1hour': 7,    // Approx 6.5 hours, rounded up
        '4hour': 2     // Approx 6.5 hours, rounded up
    };

    const durationMultipliers = {
        '1day': 1,
        '3day': 3,
        '1week': 5,     // 5 trading days
        '1month': 20,   // 20 trading days
        '3month': 60,   // 60 trading days
        '6month': 120,  // 120 trading days
        '1year': 252,   // 252 trading days
        '5year': 1260   // 5 * 252 trading days
    };

    const intervalPoints = pointsPerInterval[timeWindow] || pointsPerInterval['15min']; // Default to 15min if unknown
    const durationDays = durationMultipliers[timeDuration] || durationMultipliers['1month']; // Default to 1month if unknown

    const expectedPoints = intervalPoints * durationDays;

    // Since historicalData is already oldest_to-newest, take the last 'expectedPoints'
    return data.slice(Math.max(data.length - expectedPoints, 0));
};

// Compact metric card component
const MetricCard = ({ label, value, subValue, color }) => (
    <div className="text-center p-2 border rounded bg-light h-100">
        <small className="text-muted d-block">{label}</small>
        <strong className={`fs-6 ${color || ''}`}>{value}</strong>
        {subValue && <small className="text-muted d-block">{subValue}</small>}
    </div>
);

// Investment Signal Card
const SignalCard = ({ signal, strength, description, color }) => (
    <div className={`p-2 border rounded h-100 ${color === 'success' ? 'bg-success bg-opacity-10 border-success' : 
                                                  color === 'danger' ? 'bg-danger bg-opacity-10 border-danger' : 
                                                  'bg-warning bg-opacity-10 border-warning'}`}>
        <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="fw-bold">{signal}</small>
            <span className={`badge bg-${color}`}>{strength}</span>
        </div>
        <small className="text-muted">{description}</small>
    </div>
);

// Price Target Card
const PriceTargetCard = ({ current, target, upside, color }) => (
    <div className={`text-center p-2 border rounded h-100 ${color === 'success' ? 'bg-success bg-opacity-10 border-success' : 
                                                              color === 'danger' ? 'bg-danger bg-opacity-10 border-danger' : 
                                                              'bg-warning bg-opacity-10 border-warning'}`}>
        <small className="text-muted d-block">Price Target</small>
        <strong className="fs-6">${target}</strong>
        <small className={`d-block text-${color}`}>{upside}% upside</small>
        <small className="text-muted">Current: ${current}</small>
    </div>
);

// Grades Gauge Chart Component (defined within StockDetail for simplicity)
const GradesGaugeChart = ({ gradesSummary, getConsensusColor }) => {
    const totalRatings = gradesSummary.strongBuy + gradesSummary.buy + gradesSummary.hold + gradesSummary.sell + gradesSummary.strongSell;

    const data = {
        labels: ['Strong Buy', 'Buy', 'Hold', 'Sell', 'Strong Sell'],
        datasets: [{
            data: [gradesSummary.strongBuy, gradesSummary.buy, gradesSummary.hold, gradesSummary.sell, gradesSummary.strongSell],
            backgroundColor: [
                '#28a745', // Strong Buy (green)
                '#007bff', // Buy (blue)
                '#ffc107', // Hold (yellow)
                '#dc3545', // Sell (red)
                '#343a40', // Strong Sell (dark)
            ],
            borderColor: '#ffffff',
            borderWidth: 1,
        }]
    };

    const textCenter = {
        id: 'textCenter',
        beforeDatasetsDraw(chart, args, pluginOptions) {
            const { ctx, data } = chart;
            ctx.save();
            ctx.font = 'bolder 20px sans-serif';
            ctx.fillStyle = getConsensusColor(gradesSummary.consensus) === 'warning' ? '#212529' : 'white';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const x = chart.getDatasetMeta(0).data[0].x;
            const y = chart.getDatasetMeta(0).data[0].y; // Base Y position (center of the full circle)

            ctx.fillText(gradesSummary.consensus, x, y + 20); // Move consensus text down from the center
            
            ctx.font = 'normal 12px sans-serif';
            ctx.fillStyle = '#6c757d';
            ctx.fillText(`Total: ${totalRatings}`, x, y + 40); // Move total text further down
        }
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true, // Re-enable default legend
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += context.parsed;
                        }
                        return label;
                    }
                }
            },
            textCenter: textCenter, // Register the custom plugin
        },
        cutout: '70%', // Make the center hollow
        circumference: 180, // Half circle
        rotation: -90, // Starts from the bottom
    };

    return (
        <div style={{ height: '200px', width: '100%', position: 'relative' }}>
            <Doughnut data={data} options={options} plugins={[textCenter]} />
        </div>
    );
};

const StockDetail = () => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    // Suppress 404 errors for missing API endpoints
    const originalConsoleError = console.error;
    React.useEffect(() => {
        console.error = (...args) => {
            const message = args[0];
            if (typeof message === 'string' && (
                message.includes('404') || 
                message.includes('Failed to load resource') ||
                message.includes('the server responded with a status of 404')
            )) {
                return; // Suppress 404 errors
            }
            originalConsoleError.apply(console, args);
        };
        
        return () => {
            console.error = originalConsoleError;
        };
            }, []);
    
        const { symbol } = useParams();    const [stockData, setStockData] = useState(null);
    const [historicalData, setHistoricalData] = useState(null);
    const [newsData, setNewsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [activeFinancialTab, setActiveFinancialTab] = useState('income');
    const [analystEstimates, setAnalystEstimates] = useState([]);
    const [ratingsSnapshot, setRatingsSnapshot] = useState({});
    const [countryAllocations, setCountryAllocations] = useState([]);
    const [gradesSummary, setGradesSummary] = useState(null);
    const [isMutualFund, setIsMutualFund] = useState(false); // New state for mutual fund check
    const [selectedTimeWindow, setSelectedTimeWindow] = useState('5min'); // Set default time window to 5min
    const [selectedTimeDuration, setSelectedTimeDuration] = useState('1day'); // Set default time duration to 1day
    const [chartIndicators, setChartIndicators] = useState({
        sma50: true,
        sma200: true,
        bollinger: false,
        rsi: true,
        macd: false,
        stochastic: false,
        priceChange: false,
        volumeAnalysis: false,
        supportResistance: true,
        atr: false,
        adx: false,
        obv: false,
    });

    // Paper Trading State
    const [showTradeModal, setShowTradeModal] = useState(false);
    const [tradeType, setTradeType] = useState('BUY');
    const [orderType, setOrderType] = useState('MARKET');
    const [tradeQuantity, setTradeQuantity] = useState(1);
    const [stopLoss, setStopLoss] = useState('');
    const [takeProfit, setTakeProfit] = useState('');
    const [limitPrice, setLimitPrice] = useState('');
    const [limitExpiry, setLimitExpiry] = useState('');
    const [limitExpiryPreset, setLimitExpiryPreset] = useState('today');
    const [currentHolding, setCurrentHolding] = useState(null);
    const [portfolioBalance, setPortfolioBalance] = useState(null);
    const [tradeLoading, setTradeLoading] = useState(false);
    const [tradeError, setTradeError] = useState(null);
    const [profileId, setProfileId] = useState(null);
    const [tradeMarkers, setTradeMarkers] = useState([]);

    useEffect(() => {
        const fetchStockData = async () => {
            setLoading(true);
            setError(null);

            try {
                const user = JSON.parse(localStorage.getItem('user'));
                const token = user ? user.access_token : null;
                const headers = { 'Authorization': `Bearer ${token}` };

                // Determine if it's a mutual fund
                const assetTypeResponse = await fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/asset-type`, { headers });
                const assetTypeData = assetTypeResponse.ok ? await assetTypeResponse.json() : { asset_type: 'stock' };
                const isMF = assetTypeData.asset_type === 'mutual_fund';
                setIsMutualFund(isMF);

                // Fetch all data in parallel
                const profileCacheKey = `fmp_${symbol}_profile`;
                const incomeStatementCacheKey = `fmp_${symbol}_incomeStatement`;
                const balanceSheetCacheKey = `fmp_${symbol}_balanceSheet`;
                const cashFlowCacheKey = `fmp_${symbol}_cashFlow`;
                const historicalPriceCacheKey = `fmp_${symbol}_historicalChart_${selectedTimeWindow}_${selectedTimeDuration}`;
                const newsCacheKey = `fmp_${symbol}_news`;
                const analystEstimatesCacheKey = `fmp_${symbol}_analystEstimates`;
                const ratingsSnapshotCacheKey = `fmp_${symbol}_ratingsSnapshot`;
                const countryAllocationsCacheKey = `fmp_${symbol}_countryAllocations`;
                const gradesSummaryCacheKey = `fmp_${symbol}_gradesSummary`;

                let profileData = getCache(profileCacheKey);
                let incomeStatementData = getCache(incomeStatementCacheKey);
                let balanceSheetData = getCache(balanceSheetCacheKey);
                let cashFlowData = getCache(cashFlowCacheKey);
                let historicalPriceData = getCache(historicalPriceCacheKey);
                let newsDataFetched = getCache(newsCacheKey, 30);
                let analystEstimatesData = getCache(analystEstimatesCacheKey);
                let ratingsSnapshotData = getCache(ratingsSnapshotCacheKey);
                let countryAllocationsData = getCache(countryAllocationsCacheKey);
                let gradesSummaryData = getCache(gradesSummaryCacheKey);

                // Fetch data - use Promise.all for parallel fetching
                const results = await Promise.all([
                    profileData ? Promise.resolve(profileData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/profile`, { headers })
                            .then(r => r.ok ? r.json() : null)
                            .then(data => { if (data) setCache(profileCacheKey, data); return data; })
                            .catch(() => null),
                    (isMF || incomeStatementData) ? Promise.resolve(incomeStatementData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/income-statement`, { headers })
                            .then(r => r.ok ? r.json() : null)
                            .then(data => { if (data) setCache(incomeStatementCacheKey, data); return data; })
                            .catch(() => null),
                    (isMF || balanceSheetData) ? Promise.resolve(balanceSheetData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/balance-sheet`, { headers })
                            .then(r => r.ok ? r.json() : Promise.reject(r))
                            .then(data => { setCache(balanceSheetCacheKey, data); return data; }),
                    (isMF || cashFlowData) ? Promise.resolve(cashFlowData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/cash-flow`, { headers })
                            .then(r => r.ok ? r.json() : Promise.reject(r))
                            .then(data => { setCache(cashFlowCacheKey, data); return data; }),
                    historicalPriceData ? Promise.resolve(historicalPriceData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/historical-chart?time_window=${selectedTimeWindow}&time_duration=${selectedTimeDuration}`, { headers })
                            .then(r => r.ok ? r.json() : Promise.resolve([]))
                            .then(data => { setCache(historicalPriceCacheKey, data); return data; })
                            .catch(err => { console.warn('Historical data unavailable:', err); return []; }),
                    newsDataFetched ? Promise.resolve(newsDataFetched) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/news?limit=10`, { headers })
                            .then(r => r.ok ? r.json() : [])
                            .then(data => { setCache(newsCacheKey, data); return data; }),
                    (isMF || analystEstimatesData) ? Promise.resolve(analystEstimatesData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/analyst-estimates`, { headers })
                            .then(r => r.ok ? r.json() : [])
                            .then(data => { setCache(analystEstimatesCacheKey, data); return data; }),
                    (isMF || ratingsSnapshotData) ? Promise.resolve(ratingsSnapshotData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/ratings-snapshot`, { headers })
                            .then(r => r.ok ? r.json() : [])
                            .then(data => { setCache(ratingsSnapshotCacheKey, data); return data; }),
                    countryAllocationsData ? Promise.resolve(countryAllocationsData) :
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/etf-country-allocation`, { headers })
                            .then(r => r.ok ? r.json() : [])
                            .then(data => { setCache(countryAllocationsCacheKey, data); return data; }),
                    (isMF || gradesSummaryData) ? Promise.resolve(gradesSummaryData) : // Fetch grades summary
                        fetch(`${API_BASE}/api/stock-market/fmp/${symbol}/grades-consensus`, { headers })
                            .then(r => r.ok ? r.json() : [])
                            .then(data => { setCache(gradesSummaryCacheKey, data); return data; })
                ]);

                profileData = results[0];
                incomeStatementData = results[1];
                balanceSheetData = results[2];
                cashFlowData = results[3];
                historicalPriceData = results[4];
                newsDataFetched = results[5];
                analystEstimatesData = results[6];
                ratingsSnapshotData = results[7];
                countryAllocationsData = results[8];
                gradesSummaryData = results[9];

                // Normalize profileData: if it's an object, wrap it in an array for consistent processing
                const normalizedProfileData = (profileData && !Array.isArray(profileData)) ? [profileData] : profileData;

                if (normalizedProfileData && normalizedProfileData.length > 0) {
                    setStockData({
                        profile: normalizedProfileData[0],
                        financials: {
                            incomeStatement: incomeStatementData,
                            balanceSheet: balanceSheetData,
                            cashFlow: cashFlowData,
                        }
                    });
                    setHistoricalData((historicalPriceData || []).slice().reverse()); // Ensure historicalData is oldest to newest
                    setNewsData(newsDataFetched || []);
                    setAnalystEstimates(analystEstimatesData || []);
                    
                    const finalRatingsSnapshot = ratingsSnapshotData || {};
                    setRatingsSnapshot(finalRatingsSnapshot);
                    setCountryAllocations(countryAllocationsData || []);
                    setGradesSummary(gradesSummaryData || null);
                } else {
                    setError('Stock data not found.');
                }
            } catch (err) {
                try {
                    const errorJson = JSON.parse(err.message);
                    setError(`Error: ${errorJson.detail || 'Failed to fetch stock data'}`);
                } catch {
                    setError(`Network error: ${err.message || 'Unknown error'}`);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchStockData();

        const refreshIntervalMs = (STOCK_DETAIL_CONFIG.refreshIntervalMinutes || 5) * 60 * 1000;
        const refreshTimer = setInterval(fetchStockData, refreshIntervalMs);

        return () => clearInterval(refreshTimer);
    }, [symbol, selectedTimeWindow, selectedTimeDuration]);

    // Fetch paper trading data
    useEffect(() => {
        const fetchPaperTradingData = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                const token = user ? user.access_token : null;
                if (!token) return;

                const headers = { 'Authorization': `Bearer ${token}` };

                // Get active profile (MARKET_WATCH type)
                const profilesResponse = await fetch(`${API_BASE}/api/profiles`, { headers });
                if (!profilesResponse.ok) return;

                const profiles = await profilesResponse.json();
                const marketProfile = profiles.find(p => p.profile_type === 'MARKET_WATCH');
                if (!marketProfile) return;

                setProfileId(marketProfile.id);

                // Fetch portfolio, holding and symbol trade history in parallel
                const [portfolioRes, holdingRes, tradesRes] = await Promise.all([
                    fetch(`${API_BASE}/api/paper-trading/profiles/${marketProfile.id}/portfolio`, { headers }),
                    fetch(`${API_BASE}/api/paper-trading/profiles/${marketProfile.id}/holdings/${symbol}`, { headers }),
                    fetch(
                        `${API_BASE}/api/paper-trading/profiles/${marketProfile.id}/trades?ticker_symbol=${encodeURIComponent(symbol)}&status=CLOSED&limit=200`,
                        { headers }
                    )
                ]);

                if (portfolioRes.ok) {
                    const portfolioData = await portfolioRes.json();
                    setPortfolioBalance(portfolioData.current_balance);
                }

                if (holdingRes.ok) {
                    const holdingData = await holdingRes.json();
                    setCurrentHolding(holdingData);
                }

                if (tradesRes.ok) {
                    const tradesData = await tradesRes.json();
                    const markers = (Array.isArray(tradesData) ? tradesData : [])
                        .map((trade) => {
                            const rawTime = trade.closed_at || trade.created_at;
                            if (!rawTime) {
                                return null;
                            }
                            const normalizedTime = typeof rawTime === 'string'
                                ? rawTime.replace(' ', 'T')
                                : rawTime;
                            const timestampMs = new Date(normalizedTime).getTime();
                            if (!Number.isFinite(timestampMs)) {
                                return null;
                            }
                            const type = String(trade.trade_type || '').toUpperCase();
                            if (type !== 'BUY' && type !== 'SELL') {
                                return null;
                            }
                            const price = type === 'BUY'
                                ? trade.entry_price
                                : (trade.exit_price ?? trade.entry_price);

                            return {
                                tradeId: trade.id,
                                type,
                                timestampMs,
                                timeLabel: formatToLocalTime(rawTime, DATE_TIME_FORMAT_OPTIONS),
                                price: Number.isFinite(Number(price)) ? Number(price) : null,
                            };
                        })
                        .filter(Boolean);
                    setTradeMarkers(markers);
                } else {
                    setTradeMarkers([]);
                }
            } catch (error) {
                console.warn('Error fetching paper trading data:', error);
                setTradeMarkers([]);
            }
        };

        if (symbol) {
            fetchPaperTradingData();
        }
    }, [symbol, API_BASE]);

    // Trim historical data based on selected time window and duration
    const trimmedHistoricalData = trimHistoricalData(historicalData, selectedTimeWindow, selectedTimeDuration);

    const performanceLabels = trimmedHistoricalData ? trimmedHistoricalData.map(data => data.date) : [];
    const performanceLabelTimestamps = trimmedHistoricalData
        ? trimmedHistoricalData.map((data) => {
            const raw = data?.date;
            if (!raw) return null;
            const normalized = typeof raw === 'string' ? raw.replace(' ', 'T') : raw;
            const ts = new Date(normalized).getTime();
            return Number.isFinite(ts) ? ts : null;
        })
        : [];
    const performancePrices = trimmedHistoricalData ? trimmedHistoricalData.map(data => data.close) : [];
    const performanceVolumes = trimmedHistoricalData ? trimmedHistoricalData.map(data => data.volume) : [];
    const closes = trimmedHistoricalData ? trimmedHistoricalData.map(data => data.close) : [];

    // Ensure all data series are in oldest-to-newest order for consistent charting
    const orderedCloses = closes; // `closes` is already oldest-to-newest after historicalData is reversed
    
    const sma50 = calculateSMA(orderedCloses, 50);
    const sma200 = calculateSMA(orderedCloses, 200);
    const bollingerBands = calculateBollingerBands(trimmedHistoricalData, 20, 2);
    const macd = calculateMACD(trimmedHistoricalData, 12, 26, 9);
    const stochastic = calculateStochastic(trimmedHistoricalData, 14, 3);
    const atrSeries = calculateATRSeries(trimmedHistoricalData, 14);
    const adxSeries = calculateADXSeries(trimmedHistoricalData, 14);
    const obvSeries = calculateOBVSeries(trimmedHistoricalData);

    const orderedHistoricalData = trimmedHistoricalData; // `historicalData` is already oldest-to-newest
    const rsi = orderedHistoricalData ? calculateRSI(orderedHistoricalData, 14) : [];
    const dailyPercentageChange = orderedHistoricalData ? calculateDailyPercentageChange(orderedHistoricalData) : [];



    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <div className="alert alert-danger">{error}</div>
            </Container>
        );
    }

    // Calculate all metrics
    const profile = stockData?.profile;
    const income = stockData?.financials?.incomeStatement?.[0];
    const prevIncome = stockData?.financials?.incomeStatement?.[1];
    const balance = stockData?.financials?.balanceSheet?.[0];
    const cashFlow = stockData?.financials?.cashFlow?.[0];

    const peRatio = income?.eps ? (profile.price / income.eps).toFixed(2) : '-';
    const debtToEquity = balance?.totalStockholdersEquity ? (balance.totalDebt / balance.totalStockholdersEquity).toFixed(2) : '-';
    const currentRatio = balance?.totalCurrentLiabilities ? (balance.totalCurrentAssets / balance.totalCurrentLiabilities).toFixed(2) : '-';
    const grossMargin = income?.revenue ? ((income.grossProfit / income.revenue) * 100).toFixed(1) + '%' : '-';
    const netMargin = income?.revenue ? ((income.netIncome / income.revenue) * 100).toFixed(1) + '%' : '-';
    const psRatio = income?.revenue ? (profile.marketCap / income.revenue).toFixed(2) : '-';
    const pbRatio = balance?.totalStockholdersEquity ? (profile.marketCap / balance.totalStockholdersEquity).toFixed(2) : '-';

    const calcGrowth = (curr, prev) => prev ? (((curr - prev) / Math.abs(prev)) * 100).toFixed(1) + '%' : '-';
    const revenueGrowth = prevIncome ? calcGrowth(income.revenue, prevIncome.revenue) : '-';
    const epsGrowth = prevIncome?.eps ? calcGrowth(income.eps, prevIncome.eps) : '-';

    // Investment Analysis
    const volatility = calculateVolatility(trimmedHistoricalData);
    const sharpeRatio = calculateSharpeRatio(trimmedHistoricalData);
    const support = calculateSupport(trimmedHistoricalData);
    const resistance = calculateResistance(trimmedHistoricalData);
    const investmentSignals = getInvestmentSignals(trimmedHistoricalData, profile.price, rsi, sma50, sma200, bollingerBands, macd, stochastic, atrSeries, adxSeries, obvSeries);
    
    // Additional variables for the enhanced metrics
    const latest50SMA = sma50[sma50.length - 1];
    const latest200SMA = sma200[sma200.length - 1];
    
    // Price Target Calculation (simple technical analysis)
    const priceTarget = resistance ? resistance * 1.05 : profile.price * 1.15;
    const upside = ((priceTarget - profile.price) / profile.price * 100).toFixed(1);

    const getRsiColor = (val) => {
        if (!val) return '';
        const n = parseFloat(val);
        if (n > 70) return 'text-danger';
        if (n < 30) return 'text-success';
        return '';
    };

    const getPriceChangeColor = (change) => {
        if (change > 0) return 'text-success';
        if (change < 0) return 'text-danger';
        return 'text-muted';
    };

    const getConsensusColor = (consensus) => {
        switch (consensus) {
            case 'Strong Buy':
                return 'success';
            case 'Buy':
                return 'primary';
            case 'Hold':
                return 'warning';
            case 'Sell':
                return 'danger';
            case 'Strong Sell':
                return 'dark';
            default:
                return 'secondary';
        }
    };

    const getVolatilityColor = (vol) => {
        if (!vol) return '';
        if (vol > 30) return 'text-danger';
        if (vol < 15) return 'text-success';
        return 'text-warning';
    };

    ChartJS.register(ArcElement); // Register ArcElement for Doughnut charts

    // Paper Trading Functions
    const openTradeModal = (type) => {
        setTradeType(type);
        setOrderType('MARKET');
        setTradeQuantity(1);
        setStopLoss('');
        setTakeProfit('');
        setLimitPrice('');
        setLimitExpiry('');
        setLimitExpiryPreset('today');
        setTradeError(null);
        setShowTradeModal(true);
    };

    const addMonths = (date, months) => {
        const next = new Date(date);
        const day = next.getDate();
        next.setMonth(next.getMonth() + months);
        if (next.getDate() < day) {
            next.setDate(0);
        }
        return next;
    };

    const computeExpiryFromPreset = (preset) => {
        if (preset === 'infinite') {
            return null;
        }
        const now = new Date();
        const target = new Date(now);
        if (preset === 'today') {
            target.setHours(16, 0, 0, 0);
            if (now > target) {
                target.setDate(target.getDate() + 1);
                target.setHours(16, 0, 0, 0);
            }
        } else if (preset === 'tomorrow') {
            target.setDate(target.getDate() + 1);
            target.setHours(16, 0, 0, 0);
        } else if (preset === 'week') {
            target.setDate(target.getDate() + 7);
            target.setHours(16, 0, 0, 0);
        } else if (preset === 'month') {
            const next = addMonths(target, 1);
            next.setHours(16, 0, 0, 0);
            return next.toISOString();
        } else if (preset === 'quarter') {
            const next = addMonths(target, 3);
            next.setHours(16, 0, 0, 0);
            return next.toISOString();
        } else if (preset === 'year_end') {
            target.setMonth(11, 31);
            target.setHours(16, 0, 0, 0);
        }
        return target.toISOString();
    };

    const handleExpiryPresetChange = (value) => {
        setLimitExpiryPreset(value);
        const computed = computeExpiryFromPreset(value);
        setLimitExpiry(computed || '');
    };

    const createNotification = async ({ title, message, details, notificationType, sourceType, sourceId, severity }) => {
        if (!profileId) return;
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const token = user ? user.access_token : null;
            if (!token) return;
            await fetch(
                `${API_BASE}/api/notifications/profiles/${profileId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        title,
                        message,
                        details,
                        notification_type: notificationType,
                        severity: severity || 'LOW',
                        source_type: sourceType || null,
                        source_id: sourceId || null
                    })
                }
            );
        } catch (error) {
            console.error('Failed to create notification:', error);
        }
    };

    const executeTrade = async () => {
        if (!profileId) {
            setTradeError('No market watch profile found. Please create one first.');
            return;
        }

        if (tradeQuantity <= 0) {
            setTradeError('Quantity must be positive');
            return;
        }

        setTradeLoading(true);
        setTradeError(null);

        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const token = user ? user.access_token : null;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const endpoint = tradeType === 'BUY'
                ? `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/buy`
                : `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/sell`;

            const body = tradeType === 'BUY'
                ? {
                    ticker_symbol: symbol,
                    quantity: parseInt(tradeQuantity),
                    price: profile?.price,  // Pass current price as fallback
                    order_type: orderType,
                    limit_price: orderType === 'LIMIT' ? parseFloat(limitPrice) : null,
                    expires_at: orderType === 'LIMIT' && limitExpiry ? new Date(limitExpiry).toISOString() : null,
                    stop_loss: stopLoss ? parseFloat(stopLoss) : null,
                    take_profit: takeProfit ? parseFloat(takeProfit) : null
                }
                : {
                    ticker_symbol: symbol,
                    quantity: parseInt(tradeQuantity),
                    price: profile?.price,  // Pass current price as fallback
                    order_type: orderType,
                    limit_price: orderType === 'LIMIT' ? parseFloat(limitPrice) : null,
                    expires_at: orderType === 'LIMIT' && limitExpiry ? new Date(limitExpiry).toISOString() : null
                };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Trade execution failed');
            }

            // Update local state
            if (data.portfolio_balance !== undefined) {
                setPortfolioBalance(data.portfolio_balance);
            }

            // Refresh holding data
            const holdingRes = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/holdings/${symbol}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (holdingRes.ok) {
                const holdingData = await holdingRes.json();
                setCurrentHolding(holdingData);
            }

            const tradeStatus = data.status || data.trade?.status;
            if (tradeStatus === 'OPEN') {
                toast.info(data.message || 'Limit order queued');
            } else {
                toast.success(data.message || `${tradeType} order executed: ${tradeQuantity} shares of ${symbol}`);
                notifyTradeExecution(
                    'Trade Executed',
                    `${tradeType} ${tradeQuantity} ${symbol} at ${profile?.price?.toFixed(2) || 'market'}`,
                    'LOW'
                );
                const executedPrice = orderType === 'LIMIT' && limitPrice
                    ? parseFloat(limitPrice)
                    : profile?.price;
                const message = `${tradeType} ${tradeQuantity} ${symbol} executed${executedPrice ? ` at $${executedPrice.toFixed(2)}` : ''}`;
                const details = [
                    `Order: ${orderType}`,
                    executedPrice ? `Price: $${executedPrice.toFixed(2)}` : 'Price: market',
                    tradeType === 'BUY' && stopLoss ? `Stop Loss: $${parseFloat(stopLoss).toFixed(2)}` : null,
                    tradeType === 'BUY' && takeProfit ? `Take Profit: $${parseFloat(takeProfit).toFixed(2)}` : null
                ].filter(Boolean).join(' • ');
                createNotification({
                    title: 'Trade Executed',
                    message,
                    details,
                    notificationType: 'trade_executed',
                    sourceType: 'trade',
                    sourceId: data.trade?.id ? `${data.trade.id}` : null,
                    severity: 'LOW'
                });
            }
            localStorage.setItem('paperTradingUpdatedAt', Date.now().toString());
            window.dispatchEvent(new Event('paperTradingUpdated'));
            setShowTradeModal(false);
        } catch (error) {
            setTradeError(error.message);
        } finally {
            setTradeLoading(false);
        }
    };

    const effectivePrice = orderType === 'LIMIT' && limitPrice ? parseFloat(limitPrice) : profile?.price;
    const totalCost = effectivePrice ? (effectivePrice * tradeQuantity).toFixed(2) : 0;
    const canAffordBuy = portfolioBalance !== null && effectivePrice && portfolioBalance >= (effectivePrice * tradeQuantity);
    const canSell = currentHolding?.quantity >= tradeQuantity;
    const hasValidLimit = orderType !== 'LIMIT' || (limitPrice && parseFloat(limitPrice) > 0);

    return (
        <Container fluid className="py-3">
            {/* Header Section */}
            {stockData && (
                <Card className="mb-3 border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <Row className="align-items-center">
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-3">
                                    {profile.image && (
                                        <img src={profile.image} alt={`${profile.companyName} logo`} className="img-fluid rounded-circle" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                    )}
                                    <div>
                                        <h4 className="mb-0">{profile.companyName}</h4>
                                        <span className="text-muted">{profile.symbol}</span>
                                        <Badge bg="secondary" className="ms-2">{profile.exchangeShortName}</Badge>
                                        <Badge bg="info" className="ms-1">{profile.sector}</Badge>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="text-md-end mt-2 mt-md-0">
                                <h3 className="mb-0">
                                    {profile.currency} {profile.price?.toFixed(2)}
                                    {profile.change !== null && profile.change !== undefined && (
                                        <>
                                            <span className={`ms-2 ${getPriceChangeColor(profile.change)}`}>
                                                {profile.change > 0 ? '+' : ''}{profile.change?.toFixed(2)}
                                            </span>
                                            <span className={`ms-1 ${getPriceChangeColor(profile.change)}`}>
                                                ({profile.changePercentage?.toFixed(2)}%)
                                            </span>
                                        </>
                                    )}
                                </h3>
                                <small className="text-muted">
                                    52W: {profile.range} | Vol: {formatCompact(profile.volume)} | Avg Vol: {formatCompact(profile.averageVolume)}
                                </small>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Paper Trading Card */}
            {stockData && profileId && (
                <Card className="mb-3 shadow-sm border-primary">
                    <Card.Header className="py-2 bg-primary text-white d-flex justify-content-between align-items-center">
                        <strong>Paper Trading</strong>
                        {portfolioBalance !== null && (
                            <Badge bg="light" text="dark">
                                Balance: ${portfolioBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Badge>
                        )}
                    </Card.Header>
                    <Card.Body className="py-2">
                        <Row className="align-items-center">
                            {/* Current Position */}
                            <Col md={6}>
                                {currentHolding && currentHolding.quantity > 0 ? (
                                    <div className="p-2 bg-light rounded">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <small className="text-muted">Your Position</small>
                                            <Badge bg="info">{currentHolding.quantity} shares</Badge>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <small>Avg Cost: ${currentHolding.average_cost?.toFixed(2)}</small>
                                            <small>Value: ${currentHolding.current_value?.toFixed(2) || '-'}</small>
                                        </div>
                                        {currentHolding.profit_loss !== null && (
                                            <div className={`text-end ${currentHolding.profit_loss >= 0 ? 'text-success' : 'text-danger'}`}>
                                                <small>
                                                    P&L: {currentHolding.profit_loss >= 0 ? '+' : ''}${currentHolding.profit_loss?.toFixed(2)}
                                                    ({currentHolding.profit_loss_percent?.toFixed(2)}%)
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="p-2 bg-light rounded text-center">
                                        <small className="text-muted">No current position in {symbol}</small>
                                    </div>
                                )}
                            </Col>
                            {/* Trade Buttons */}
                            <Col md={6} className="text-end">
                                <ButtonGroup>
                                    <Button
                                        variant="success"
                                        onClick={() => openTradeModal('BUY')}
                                        className="px-4"
                                    >
                                        Buy
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => openTradeModal('SELL')}
                                        disabled={!currentHolding || currentHolding.quantity === 0}
                                        className="px-4"
                                    >
                                        Sell
                                    </Button>
                                </ButtonGroup>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Trade Modal */}
            <Modal show={showTradeModal} onHide={() => setShowTradeModal(false)} centered>
                <Modal.Header closeButton className={tradeType === 'BUY' ? 'bg-success text-white' : 'bg-danger text-white'}>
                    <Modal.Title>{tradeType} {symbol}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {tradeError && (
                        <Alert variant="danger" dismissible onClose={() => setTradeError(null)}>
                            {tradeError}
                        </Alert>
                    )}

                    <div className="mb-3 p-2 bg-light rounded text-center">
                        <small className="text-muted d-block">Current Price</small>
                        <strong className="fs-4">${profile?.price?.toFixed(2)}</strong>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Order Type</Form.Label>
                    <Form.Select
                        value={orderType}
                        onChange={(e) => {
                            const value = e.target.value;
                            setOrderType(value);
                            if (value === 'LIMIT') {
                                handleExpiryPresetChange(limitExpiryPreset || 'today');
                            }
                        }}
                    >
                            <option value="MARKET">Market</option>
                            <option value="LIMIT">Limit</option>
                        </Form.Select>
                    </Form.Group>

                    {orderType === 'LIMIT' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Limit Price</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="Enter limit price"
                                    value={limitPrice}
                                    onChange={(e) => setLimitPrice(e.target.value)}
                                />
                            </InputGroup>
                        </Form.Group>
                    )}
                    {orderType === 'LIMIT' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Valid Until</Form.Label>
                            <Form.Select
                                value={limitExpiryPreset}
                                onChange={(e) => handleExpiryPresetChange(e.target.value)}
                            >
                                <option value="today">Today (End of Trading Day)</option>
                                <option value="tomorrow">Tomorrow (End of Trading Day)</option>
                                <option value="week">1 Week</option>
                                <option value="month">1 Month</option>
                                <option value="quarter">3 Months</option>
                                <option value="year_end">End of This Year</option>
                                <option value="infinite">Infinite</option>
                            </Form.Select>
                            <Form.Text className="text-muted d-block mt-1">
                                {limitExpiryPreset === 'infinite'
                                    ? 'Order will remain active until manually canceled.'
                                    : `Expires at: ${limitExpiry ? new Date(limitExpiry).toLocaleString('en-US') : '-'}`}
                            </Form.Text>
                            <Form.Text className="text-muted">
                                Order will be canceled if not filled by this time.
                            </Form.Text>
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            value={tradeQuantity}
                            onChange={(e) => setTradeQuantity(parseInt(e.target.value) || 0)}
                        />
                        {tradeType === 'SELL' && currentHolding && (
                            <Form.Text className="text-muted">
                                Available: {currentHolding.quantity} shares
                            </Form.Text>
                        )}
                    </Form.Group>

                    <div className="mb-3 p-2 border rounded">
                        <div className="d-flex justify-content-between">
                            <span>Total {tradeType === 'BUY' ? 'Cost' : 'Proceeds'}:</span>
                            <strong>${totalCost}</strong>
                        </div>
                        {tradeType === 'BUY' && portfolioBalance !== null && (
                            <div className="d-flex justify-content-between text-muted">
                                <small>Available Balance:</small>
                                <small>${portfolioBalance.toFixed(2)}</small>
                            </div>
                        )}
                    </div>

                    {tradeType === 'BUY' && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Stop Loss (Optional)</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., below current price"
                                        value={stopLoss}
                                        onChange={(e) => setStopLoss(e.target.value)}
                                    />
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Auto-sell if price drops to this level
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Take Profit (Optional)</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="e.g., above current price"
                                        value={takeProfit}
                                        onChange={(e) => setTakeProfit(e.target.value)}
                                    />
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Auto-sell if price rises to this level
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}

                    {orderType === 'LIMIT' && !hasValidLimit && (
                        <Alert variant="warning" className="mb-2">
                            Enter a valid limit price
                        </Alert>
                    )}
                    {tradeType === 'BUY' && !canAffordBuy && (
                        <Alert variant="warning" className="mb-0">
                            Insufficient balance for this trade
                        </Alert>
                    )}
                    {tradeType === 'SELL' && !canSell && (
                        <Alert variant="warning" className="mb-0">
                            Insufficient shares for this trade
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTradeModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant={tradeType === 'BUY' ? 'success' : 'danger'}
                        onClick={executeTrade}
                        disabled={tradeLoading || !hasValidLimit || (tradeType === 'BUY' && !canAffordBuy) || (tradeType === 'SELL' && !canSell)}
                    >
                        {tradeLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            `Confirm ${tradeType}`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Key Metrics Dashboard */}
            {stockData && (
                <Row className="g-2 mb-3">
                    <Col xs={6} sm={4} md={2}><MetricCard label="Market Cap" value={formatCompact(profile.marketCap)} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="P/E Ratio" value={peRatio} color={peRatio > 25 ? 'text-danger' : peRatio < 15 ? 'text-success' : ''} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="P/S Ratio" value={psRatio} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="P/B Ratio" value={pbRatio} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="D/E Ratio" value={debtToEquity} color={debtToEquity > 1 ? 'text-danger' : 'text-success'} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="Current Ratio" value={currentRatio} color={currentRatio < 1 ? 'text-danger' : 'text-success'} /></Col>
                </Row>
            )}

            {stockData && (
                <Row className="g-2 mb-3">
                    <Col xs={6} sm={4} md={2}><MetricCard label="Gross Margin" value={grossMargin} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="Net Margin" value={netMargin} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="Revenue Growth" value={revenueGrowth} color={revenueGrowth.startsWith('-') ? 'text-danger' : 'text-success'} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="EPS Growth" value={epsGrowth} color={epsGrowth.startsWith('-') ? 'text-danger' : 'text-success'} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="Beta" value={profile.beta?.toFixed(2) || '-'} color={profile.beta > 1.5 ? 'text-danger' : profile.beta < 0.8 ? 'text-success' : ''} /></Col>
                    <Col xs={6} sm={4} md={2}><MetricCard label="RSI (14)" value={rsi[rsi.length - 1]?.toFixed(2) || '-'} color={getRsiColor(rsi[rsi.length - 1])} /></Col>
                </Row>
            )}

            {/* Investment Analysis Dashboard */}
            {stockData && trimmedHistoricalData && trimmedHistoricalData.length > 0 && (
                <>
                    <Row className="g-2 mb-3">
                        <Col xs={6} sm={4} md={2}><MetricCard label="Volatility" value={volatility ? volatility.toFixed(1) + '%' : '-'} color={getVolatilityColor(volatility)} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Sharpe Ratio" value={sharpeRatio || '-'} color={sharpeRatio > 1 ? 'text-success' : sharpeRatio < 0 ? 'text-danger' : ''} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Support" value={support ? '$' + support.toFixed(2) : '-'} subValue={support ? ((profile.price - support) / support * 100).toFixed(1) + '% above' : ''} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Resistance" value={resistance ? '$' + resistance.toFixed(2) : '-'} subValue={resistance ? ((resistance - profile.price) / profile.price * 100).toFixed(1) + '% above' : ''} /></Col>
                        <Col xs={6} sm={4} md={2}><PriceTargetCard current={profile.price.toFixed(2)} target={priceTarget.toFixed(2)} upside={upside} color={upside > 10 ? 'success' : upside < -5 ? 'danger' : 'warning'} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="MACD Signal" value={macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1] ? 'Bullish' : 'Bearish'} color={macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1] ? 'text-success' : 'text-danger'} /></Col>
                    </Row>

                    <Row className="g-2 mb-3">
                        <Col xs={6} sm={4} md={2}><MetricCard label="Stochastic %K" value={stochastic.k[stochastic.k.length - 1]?.toFixed(1) || '-'} color={stochastic.k[stochastic.k.length - 1] < 20 ? 'text-success' : stochastic.k[stochastic.k.length - 1] > 80 ? 'text-danger' : ''} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="BB Position" value={bollingerBands.upper[bollingerBands.upper.length - 1] ? (((profile.price - bollingerBands.lower[bollingerBands.lower.length - 1]) / (bollingerBands.upper[bollingerBands.upper.length - 1] - bollingerBands.lower[bollingerBands.lower.length - 1])) * 100).toFixed(0) + '%' : '-'} subValue="Within bands" /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Volume Ratio" value={trimmedHistoricalData.length > 0 ? (trimmedHistoricalData[trimmedHistoricalData.length - 1].volume / (trimmedHistoricalData.slice(-20).reduce((sum, d) => sum + d.volume, 0) / 20)).toFixed(2) + 'x' : '-'} subValue="vs 20-day avg" /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Price vs SMA50" value={latest50SMA ? ((profile.price - latest50SMA) / latest50SMA * 100).toFixed(1) + '%' : '-'} color={profile.price > latest50SMA ? 'text-success' : 'text-danger'} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Price vs SMA200" value={latest200SMA ? ((profile.price - latest200SMA) / latest200SMA * 100).toFixed(1) + '%' : '-'} color={profile.price > latest200SMA ? 'text-success' : 'text-danger'} /></Col>
                        <Col xs={6} sm={4} md={2}><MetricCard label="Momentum Score" value={(() => {
                            let score = 0;
                            if (rsi[rsi.length - 1] > 50) score += 1;
                            if (profile.price > latest50SMA) score += 1;
                            if (profile.price > latest200SMA) score += 1;
                            if (macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1]) score += 1;
                            if (stochastic.k[stochastic.k.length - 1] > 50) score += 1;
                            return `${score}/5`;
                        })()} color={(() => {
                            let score = 0;
                            if (rsi[rsi.length - 1] > 50) score += 1;
                            if (profile.price > latest50SMA) score += 1;
                            if (profile.price > latest200SMA) score += 1;
                            if (macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1]) score += 1;
                            if (stochastic.k[stochastic.k.length - 1] > 50) score += 1;
                            return score >= 4 ? 'text-success' : score <= 2 ? 'text-danger' : 'text-warning';
                        })()} /></Col>
                    </Row>

                    {/* Investment Signals */}
                    {investmentSignals.length > 0 && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Investment Signals</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <Row className="g-2">
                                    {investmentSignals.map((signal, index) => (
                                        <Col key={index} xs={12} sm={6} md={4}>
                                            <SignalCard {...signal} />
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}

            {/* Main Content - Two Column Layout */}
            <Row className="g-3">
                {/* Left Column - Charts */}
                <Col lg={8}>
                    {/* Price Chart */}
                    {historicalData && historicalData.length > 0 && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom d-flex justify-content-between align-items-center">
                                <strong>Price & Technical Analysis</strong>
                                <div className="d-flex flex-wrap gap-1">
                                    {/* Time Window Selection */}
                                    <ButtonGroup size="sm">
                                        {['5min', '15min', '30min', '1hour', '4hour'].map((window) => (
                                            <Button
                                                key={window}
                                                variant={selectedTimeWindow === window ? "primary" : "outline-secondary"}
                                                onClick={() => setSelectedTimeWindow(window)}
                                                style={{ padding: '0.1rem 0.3rem', fontSize: '0.6rem' }}
                                            >
                                                {window}
                                            </Button>
                                        ))}
                                    </ButtonGroup>

                                    {/* Time Duration Selection */}
                                    <ButtonGroup size="sm">
                                        {['1day', '3day', '1week', '1month', '3month', '6month', '1year', '5year', 'alltime'].map((duration) => (
                                            <Button
                                                key={duration}
                                                variant={selectedTimeDuration === duration ? "primary" : "outline-secondary"}
                                                onClick={() => setSelectedTimeDuration(duration)}
                                                style={{ padding: '0.1rem 0.3rem', fontSize: '0.6rem' }}
                                            >
                                                {duration}
                                            </Button>
                                        ))}
                                    </ButtonGroup>

                                    {/* Chart Indicators */}
                                    <Button size="sm" variant={chartIndicators.sma50 ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, sma50: !prev.sma50}))}>SMA50</Button>
                                    <Button size="sm" variant={chartIndicators.sma200 ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, sma200: !prev.sma200}))}>SMA200</Button>
                                    <Button size="sm" variant={chartIndicators.bollinger ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, bollinger: !prev.bollinger}))}>BB</Button>
                                    <Button size="sm" variant={chartIndicators.rsi ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, rsi: !prev.rsi}))}>RSI</Button>
                                    <Button size="sm" variant={chartIndicators.macd ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, macd: !prev.macd}))}>MACD</Button>
                                    <Button size="sm" variant={chartIndicators.stochastic ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, stochastic: !prev.stochastic}))}>Stoch</Button>
                                    <Button size="sm" variant={chartIndicators.priceChange ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, priceChange: !prev.priceChange}))}>% Chg</Button>
                                    <Button size="sm" variant={chartIndicators.volumeAnalysis ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, volumeAnalysis: !prev.volumeAnalysis}))}>Vol</Button>
                                    <Button size="sm" variant={chartIndicators.supportResistance ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, supportResistance: !prev.supportResistance}))}>S/R</Button>
                                    <Button size="sm" variant={chartIndicators.atr ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, atr: !prev.atr}))}>ATR</Button>
                                    <Button size="sm" variant={chartIndicators.adx ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, adx: !prev.adx}))}>ADX</Button>
                                    <Button size="sm" variant={chartIndicators.obv ? "primary" : "outline-secondary"} onClick={() => setChartIndicators(prev => ({...prev, obv: !prev.obv}))}>OBV</Button>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-2">
                                {/* Indicator Legend */}
                                {(chartIndicators.sma50 || chartIndicators.sma200 || chartIndicators.bollinger || chartIndicators.rsi || chartIndicators.macd || chartIndicators.stochastic || chartIndicators.priceChange || chartIndicators.volumeAnalysis || chartIndicators.supportResistance || chartIndicators.atr || chartIndicators.adx || chartIndicators.obv) && (
                                    <div className="mb-2 p-2 bg-light rounded">
                                        <small className="text-muted d-block mb-1">Active Indicators:</small>
                                        <div className="d-flex flex-wrap gap-2">
                                            {chartIndicators.sma50 && <Badge bg="warning" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#ffc107'}}></div>SMA 50</Badge>}
                                            {chartIndicators.sma200 && <Badge bg="danger" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#dc3545'}}></div>SMA 200</Badge>}
                                            {chartIndicators.bollinger && <Badge bg="secondary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#6c757d'}}></div>Bollinger Bands</Badge>}
                                            {chartIndicators.rsi && <Badge bg="success" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#28a745'}}></div>RSI (30/70)</Badge>}
                                            {chartIndicators.macd && <Badge bg="info" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#007bff'}}></div>MACD</Badge>}
                                            {chartIndicators.stochastic && <Badge bg="dark" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#343a40'}}></div>Stochastic (20/80)</Badge>}
                                            {chartIndicators.priceChange && <Badge bg="primary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#0d6efd'}}></div>% Change</Badge>}
                                            {chartIndicators.volumeAnalysis && <Badge bg="secondary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#6c757d'}}></div>Volume Heat</Badge>}
                                            {chartIndicators.supportResistance && <Badge bg="light" text="dark" className="d-flex align-items-center gap-1 border"><div style={{width: '8px', height: '2px', backgroundColor: '#198754'}}></div>Support/Resistance</Badge>}
                                            {chartIndicators.atr && <Badge bg="secondary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#6f42c1'}}></div>ATR</Badge>}
                                            {chartIndicators.adx && <Badge bg="secondary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#20c997'}}></div>ADX</Badge>}
                                            {chartIndicators.obv && <Badge bg="secondary" className="d-flex align-items-center gap-1"><div style={{width: '8px', height: '2px', backgroundColor: '#fd7e14'}}></div>OBV</Badge>}
                                        </div>
                                    </div>
                                )}
                                
                                <StockPerformanceChart
                                    title=""
                                    labels={performanceLabels}
                                    labelTimestamps={performanceLabelTimestamps}
                                    prices={performancePrices}
                                    volumes={performanceVolumes}
                                    tradeMarkers={tradeMarkers}
                                    currency={profile.currency}
                                    sma50={sma50}
                                    sma200={sma200}
                                    rsi={rsi}
                                    dailyPercentageChange={dailyPercentageChange}
                                    bollingerBands={bollingerBands}
                                    macd={macd}
                                    stochastic={stochastic}
                                    support={support}
                                    resistance={resistance}
                                    atrSeries={atrSeries}
                                    adxSeries={adxSeries}
                                    obvSeries={obvSeries}
                                    showSMA50={chartIndicators.sma50}
                                    showSMA200={chartIndicators.sma200}
                                    showBollingerBands={chartIndicators.bollinger}
                                    showRSI={chartIndicators.rsi}
                                    showMACD={chartIndicators.macd}
                                    showStochastic={chartIndicators.stochastic}
                                    showPriceChange={chartIndicators.priceChange}
                                    showVolumeAnalysis={chartIndicators.volumeAnalysis}
                                    showSupportResistance={chartIndicators.supportResistance}
                                    showATR={chartIndicators.atr}
                                    showADX={chartIndicators.adx}
                                    showOBV={chartIndicators.obv}
                                />
                            </Card.Body>
                        </Card>
                    )}

                    {/* Trading Levels (move closer to price chart) */}
                    {stockData && trimmedHistoricalData && trimmedHistoricalData.length > 0 && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Key Trading Levels</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Resistance</td>
                                            <td className="text-end">
                                                <strong className="text-danger">${resistance?.toFixed(2) || '-'}</strong>
                                                {resistance && (
                                                    <small className="d-block text-muted">
                                                        {((resistance - profile.price) / profile.price * 100).toFixed(1)}% above
                                                    </small>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Current Price</td>
                                            <td className="text-end">
                                                <strong>${profile.price.toFixed(2)}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Support</td>
                                            <td className="text-end">
                                                <strong className="text-success">${support?.toFixed(2) || '-'}</strong>
                                                {support && (
                                                    <small className="d-block text-muted">
                                                        {((profile.price - support) / support * 100).toFixed(1)}% above
                                                    </small>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">SMA 50</td>
                                            <td className="text-end">
                                                <strong className={profile.price > latest50SMA ? 'text-success' : 'text-danger'}>
                                                    ${latest50SMA?.toFixed(2) || '-'}
                                                </strong>
                                                {latest50SMA && (
                                                    <small className={`d-block ${profile.price > latest50SMA ? 'text-success' : 'text-danger'}`}>
                                                        {((profile.price - latest50SMA) / latest50SMA * 100).toFixed(1)}%
                                                    </small>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">SMA 200</td>
                                            <td className="text-end">
                                                <strong className={profile.price > latest200SMA ? 'text-success' : 'text-danger'}>
                                                    ${latest200SMA?.toFixed(2) || '-'}
                                                </strong>
                                                {latest200SMA && (
                                                    <small className={`d-block ${profile.price > latest200SMA ? 'text-success' : 'text-danger'}`}>
                                                        {((profile.price - latest200SMA) / latest200SMA * 100).toFixed(1)}%
                                                    </small>
                                                )}
                                            </td>
                                        </tr>
                                        {bollingerBands.upper[bollingerBands.upper.length - 1] && (
                                            <>
                                                <tr>
                                                    <td className="text-muted">BB Upper</td>
                                                    <td className="text-end">
                                                        <strong>${bollingerBands.upper[bollingerBands.upper.length - 1].toFixed(2)}</strong>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-muted">BB Lower</td>
                                                    <td className="text-end">
                                                        <strong>${bollingerBands.lower[bollingerBands.lower.length - 1].toFixed(2)}</strong>
                                                    </td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Financial Statements with Tabs */}
                    {stockData?.financials && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Body className="p-0">
                                <Tabs
                                    activeKey={activeFinancialTab}
                                    onSelect={(k) => setActiveFinancialTab(k)}
                                    className="mb-0"
                                    fill
                                >
                                    <Tab eventKey="income" title="Income Statement">
                                        <div className="p-3">
                                            {stockData.financials.incomeStatement?.length > 0 && (
                                                <>
                                                    <div style={{ height: '250px' }}>
                                                        <FinancialChart
                                                            title=""
                                                            labels={stockData.financials.incomeStatement.map(s => s.fiscalYear).reverse()}
                                                            datasets={[
                                                                { label: 'Revenue', data: stockData.financials.incomeStatement.map(s => s.revenue).reverse(), color: '#28a745' },
                                                                { label: 'Gross Profit', data: stockData.financials.incomeStatement.map(s => s.grossProfit).reverse(), color: '#007bff' },
                                                                { label: 'Net Income', data: stockData.financials.incomeStatement.map(s => s.netIncome).reverse(), color: '#ffc107' },
                                                            ]}
                                                            currency={profile.currency}
                                                        />
                                                    </div>
                                                    <div className="table-responsive mt-3">
                                                        <table className="table table-sm table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Metric</th>
                                                                    {stockData.financials.incomeStatement.slice(0, 5).map((s, i) => (
                                                                        <th key={i} className="text-end">{s.calendarYear}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr><td>Revenue</td>{stockData.financials.incomeStatement.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.revenue)}</td>)}</tr>
                                                                <tr><td>Gross Profit</td>{stockData.financials.incomeStatement.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.grossProfit)}</td>)}</tr>
                                                                <tr><td>Operating Income</td>{stockData.financials.incomeStatement.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.operatingIncome)}</td>)}</tr>
                                                                <tr><td>Net Income</td>{stockData.financials.incomeStatement.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.netIncome)}</td>)}</tr>
                                                                <tr><td>EPS</td>{stockData.financials.incomeStatement.slice(0, 5).map((s, i) => <td key={i} className="text-end">{s.eps?.toFixed(2)}</td>)}</tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Tab>

                                    <Tab eventKey="balance" title="Balance Sheet">
                                        <div className="p-3">
                                            {stockData.financials.balanceSheet?.length > 0 && (
                                                <>
                                                    <div style={{ height: '250px' }}>
                                                        <FinancialChart
                                                            title=""
                                                            labels={stockData.financials.balanceSheet.map(s => s.fiscalYear).reverse()}
                                                            datasets={[
                                                                { label: 'Total Assets', data: stockData.financials.balanceSheet.map(s => s.totalAssets).reverse(), color: '#28a745' },
                                                                { label: 'Total Liabilities', data: stockData.financials.balanceSheet.map(s => s.totalLiabilities).reverse(), color: '#dc3545' },
                                                                { label: 'Equity', data: stockData.financials.balanceSheet.map(s => s.totalStockholdersEquity).reverse(), color: '#007bff' },
                                                            ]}
                                                            currency={profile.currency}
                                                        />
                                                    </div>
                                                    <div className="table-responsive mt-3">
                                                        <table className="table table-sm table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Metric</th>
                                                                    {stockData.financials.balanceSheet.slice(0, 5).map((s, i) => (
                                                                        <th key={i} className="text-end">{s.calendarYear}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr><td>Total Assets</td>{stockData.financials.balanceSheet.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.totalAssets)}</td>)}</tr>
                                                                <tr><td>Total Liabilities</td>{stockData.financials.balanceSheet.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.totalLiabilities)}</td>)}</tr>
                                                                <tr><td>Total Equity</td>{stockData.financials.balanceSheet.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.totalStockholdersEquity)}</td>)}</tr>
                                                                <tr><td>Total Debt</td>{stockData.financials.balanceSheet.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.totalDebt)}</td>)}</tr>
                                                                <tr><td>Cash</td>{stockData.financials.balanceSheet.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.cashAndCashEquivalents)}</td>)}</tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Tab>

                                    <Tab eventKey="cashflow" title="Cash Flow">
                                        <div className="p-3">
                                            {stockData.financials.cashFlow?.length > 0 && (
                                                <>
                                                    <Row className="g-2 mb-3">
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Operating CF"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['operatingCashFlow', 'netCashProvidedByOperatingActivities']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Investing CF"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['investingCashFlow', 'netCashProvidedByInvestingActivities', 'netCashUsedForInvestingActivites', 'netCashUsedForInvestingActivities']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Financing CF"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['financingCashFlow', 'netCashProvidedByFinancingActivities', 'netCashUsedProvidedByFinancingActivities', 'netCashUsedProvidedByFinancingActivity']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Net Change"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['netChangeInCash']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="CapEx"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['capitalExpenditure']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Free Cash Flow"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['freeCashFlow']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Net Debt Issuance"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['netDebtIssuance', 'longTermNetDebtIssuance', 'shortTermNetDebtIssuance']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Net Dividends Paid"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['netDividendsPaid', 'commonDividendsPaid', 'preferredDividendsPaid']))}
                                                            />
                                                        </Col>
                                                        <Col xs={6} sm={4} md={3}>
                                                            <MetricCard
                                                                label="Cash At End"
                                                                value={formatCompact(getFirstFieldValue(cashFlow, ['cashAtEndOfPeriod']))}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <div style={{ height: '250px' }}>
                                                        <FinancialChart
                                                            title=""
                                                            labels={stockData.financials.cashFlow.map(s => s.fiscalYear).reverse()}
                                                            datasets={[
                                                                { label: 'Operating CF', data: stockData.financials.cashFlow.map(s => getFirstFieldValue(s, ['operatingCashFlow', 'netCashProvidedByOperatingActivities'])).reverse(), color: '#28a745' },
                                                                { label: 'Investing CF', data: stockData.financials.cashFlow.map(s => getFirstFieldValue(s, ['investingCashFlow', 'netCashProvidedByInvestingActivities', 'netCashUsedForInvestingActivites', 'netCashUsedForInvestingActivities'])).reverse(), color: '#007bff' },
                                                                { label: 'Free CF', data: stockData.financials.cashFlow.map(s => s.freeCashFlow).reverse(), color: '#ffc107' },
                                                            ]}
                                                            currency={profile.currency}
                                                        />
                                                    </div>
                                                    <div className="table-responsive mt-3">
                                                        <table className="table table-sm table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Metric</th>
                                                                    {stockData.financials.cashFlow.slice(0, 5).map((s, i) => (
                                                                        <th key={i} className="text-end">{s.calendarYear}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr><td>Operating CF</td>{stockData.financials.cashFlow.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(getFirstFieldValue(s, ['operatingCashFlow', 'netCashProvidedByOperatingActivities']))}</td>)}</tr>
                                                                <tr><td>Investing CF</td>{stockData.financials.cashFlow.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(getFirstFieldValue(s, ['investingCashFlow', 'netCashProvidedByInvestingActivities', 'netCashUsedForInvestingActivites', 'netCashUsedForInvestingActivities']))}</td>)}</tr>
                                                                <tr><td>Financing CF</td>{stockData.financials.cashFlow.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(getFirstFieldValue(s, ['financingCashFlow', 'netCashProvidedByFinancingActivities', 'netCashUsedProvidedByFinancingActivities', 'netCashUsedProvidedByFinancingActivity']))}</td>)}</tr>
                                                                <tr><td>Free Cash Flow</td>{stockData.financials.cashFlow.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.freeCashFlow)}</td>)}</tr>
                                                                <tr><td>CapEx</td>{stockData.financials.cashFlow.slice(0, 5).map((s, i) => <td key={i} className="text-end">{formatCompact(s.capitalExpenditure)}</td>)}</tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </Tab>

                                    <Tab eventKey="estimates" title="Analyst Estimates">
                                        <div className="p-3">
                                            {analystEstimates && analystEstimates.length > 0 ? (
                                                <>
                                                    <Row className="g-3">
                                                        <Col md={6}>
                                                            <div style={{ height: '230px' }}>
                                                                <FinancialChart
                                                                    title=""
                                                                    labels={[...analystEstimates].reverse().map(e => e.date)}
                                                                    datasets={[
                                                                        { label: 'Estimated Revenue', data: [...analystEstimates].reverse().map(e => e.revenueAvg), color: '#007bff' },
                                                                    ]}
                                                                    currency={profile.currency}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div style={{ height: '230px' }}>
                                                                <FinancialChart
                                                                    title=""
                                                                    labels={[...analystEstimates].reverse().map(e => e.date)}
                                                                    datasets={[
                                                                        { label: 'Estimated EPS', data: [...analystEstimates].reverse().map(e => e.epsAvg), color: '#28a745' },
                                                                    ]}
                                                                    currency={profile.currency}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <div className="table-responsive mt-3">
                                                        <table className="table table-sm table-hover mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th className="text-end">Estimated Revenue</th>
                                                                    <th className="text-end">Estimated EPS</th>
                                                                    <th className="text-end">Number of Analysts</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {analystEstimates.slice(0, 10).map((estimate, index) => (
                                                                    <tr key={index}>
                                                                        <td>{estimate.date}</td>
                                                                        <td className="text-end">{formatCompact(estimate.revenueAvg)}</td>
                                                                        <td className="text-end">{estimate.epsAvg?.toFixed(2)}</td>
                                                                        <td className="text-end">{estimate.numAnalystsRevenue}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="text-muted text-center">No analyst estimates available.</p>
                                            )}
                                        </div>
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                {/* Right Column - News & Info */}
                <Col lg={4}>
                    {/* Quick Investment Summary */}
                    {stockData && gradesSummary && !isMutualFund && (
                        <Card className="mb-3 shadow-sm border-primary">
                            <Card.Header className="py-2 bg-primary text-white">
                                <strong>Investment Summary</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <div className="text-center mb-2">
                                    <h5 className={`mb-1 text-${getConsensusColor(gradesSummary.consensus)}`}>
                                        {gradesSummary.consensus}
                                    </h5>
                                    <small className="text-muted">Analyst Consensus</small>
                                </div>
                                
                                <Row className="g-1 mb-2">
                                    <Col xs={6}>
                                        <div className="text-center p-1 border rounded">
                                            <small className="text-muted d-block">Target</small>
                                            <strong className="text-success">${priceTarget.toFixed(2)}</strong>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-center p-1 border rounded">
                                            <small className="text-muted d-block">Upside</small>
                                            <strong className={upside > 0 ? 'text-success' : 'text-danger'}>{upside}%</strong>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-between align-items-center">
                                    <small>Investment Grade:</small>
                                    <Badge bg={upside > 15 && (gradesSummary.consensus || '').includes('Buy') ? 'success' : 
                                              upside > 5 || gradesSummary.consensus === 'Hold' ? 'warning' : 'danger'} 
                                           className="px-2">
                                        {upside > 15 && (gradesSummary.consensus || '').includes('Buy') ? 'STRONG BUY' : 
                                         upside > 5 || gradesSummary.consensus === 'Hold' ? 'HOLD' : 'AVOID'}
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Risk Assessment Card */}
                    {stockData && trimmedHistoricalData && trimmedHistoricalData.length > 0 && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Risk Assessment</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small>Volatility Risk</small>
                                        <Badge bg={volatility > 30 ? 'danger' : volatility > 20 ? 'warning' : 'success'}>
                                            {volatility > 30 ? 'High' : volatility > 20 ? 'Medium' : 'Low'}
                                        </Badge>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                        <div className={`progress-bar bg-${volatility > 30 ? 'danger' : volatility > 20 ? 'warning' : 'success'}`} 
                                             style={{ width: `${Math.min(volatility, 50)}%` }}></div>
                                    </div>
                                </div>
                                
                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small>Liquidity Risk</small>
                                        <Badge bg={profile.averageVolume < 1000000 ? 'danger' : profile.averageVolume < 5000000 ? 'warning' : 'success'}>
                                            {profile.averageVolume < 1000000 ? 'High' : profile.averageVolume < 5000000 ? 'Medium' : 'Low'}
                                        </Badge>
                                    </div>
                                    <small className="text-muted">Avg Volume: {formatCompact(profile.averageVolume)}</small>
                                </div>

                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small>Financial Risk</small>
                                        <Badge bg={debtToEquity > 1 ? 'danger' : debtToEquity > 0.5 ? 'warning' : 'success'}>
                                            {debtToEquity > 1 ? 'High' : debtToEquity > 0.5 ? 'Medium' : 'Low'}
                                        </Badge>
                                    </div>
                                    <small className="text-muted">D/E Ratio: {debtToEquity}</small>
                                </div>

                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small>Valuation Risk</small>
                                        <Badge bg={peRatio > 25 ? 'danger' : peRatio > 18 ? 'warning' : 'success'}>
                                            {peRatio > 25 ? 'High' : peRatio > 18 ? 'Medium' : 'Low'}
                                        </Badge>
                                    </div>
                                    <small className="text-muted">P/E Ratio: {peRatio}</small>
                                </div>

                                <hr className="my-2" />
                                <div className="text-center">
                                    <strong className={`text-${volatility > 25 || debtToEquity > 1 || peRatio > 25 ? 'danger' : 
                                                        volatility > 20 || debtToEquity > 0.5 || peRatio > 18 ? 'warning' : 'success'}`}>
                                        Overall Risk: {volatility > 25 || debtToEquity > 1 || peRatio > 25 ? 'HIGH' : 
                                                      volatility > 20 || debtToEquity > 0.5 || peRatio > 18 ? 'MEDIUM' : 'LOW'}
                                    </strong>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Market Sentiment Analysis */}
                    {stockData && trimmedHistoricalData && trimmedHistoricalData.length > 0 && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Market Sentiment</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <div className="text-center p-2 border rounded">
                                            <small className="text-muted d-block">Fear & Greed</small>
                                            <strong className={`fs-6 ${(() => {
                                                let score = 0;
                                                if (rsi[rsi.length - 1] > 70) score -= 2;
                                                else if (rsi[rsi.length - 1] < 30) score += 2;
                                                if (profile.price > latest50SMA) score += 1;
                                                if (profile.price > latest200SMA) score += 1;
                                                if (volatility < 20) score += 1;
                                                else if (volatility > 30) score -= 1;
                                                return score > 2 ? 'text-success' : score < -1 ? 'text-danger' : 'text-warning';
                                            })()}`}>
                                                {(() => {
                                                    let score = 0;
                                                    if (rsi[rsi.length - 1] > 70) score -= 2;
                                                    else if (rsi[rsi.length - 1] < 30) score += 2;
                                                    if (profile.price > latest50SMA) score += 1;
                                                    if (profile.price > latest200SMA) score += 1;
                                                    if (volatility < 20) score += 1;
                                                    else if (volatility > 30) score -= 1;
                                                    return score > 2 ? 'Greed' : score < -1 ? 'Fear' : 'Neutral';
                                                })()}
                                            </strong>
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-center p-2 border rounded">
                                            <small className="text-muted d-block">Trend Strength</small>
                                            <strong className={`fs-6 ${Math.abs(profile.price - latest50SMA) / latest50SMA > 0.05 ? 'text-success' : 'text-warning'}`}>
                                                {Math.abs(profile.price - latest50SMA) / latest50SMA > 0.05 ? 'Strong' : 'Weak'}
                                            </strong>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div className="mt-2">
                                    <small className="text-muted">Technical Score: </small>
                                    <span className={`badge bg-${(() => {
                                        let score = 0;
                                        if (rsi[rsi.length - 1] > 30 && rsi[rsi.length - 1] < 70) score += 1;
                                        if (profile.price > latest50SMA) score += 1;
                                        if (profile.price > latest200SMA) score += 1;
                                        if (macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1]) score += 1;
                                        if (stochastic.k[stochastic.k.length - 1] > 20 && stochastic.k[stochastic.k.length - 1] < 80) score += 1;
                                        return score >= 4 ? 'success' : score >= 3 ? 'warning' : 'danger';
                                    })()}`}>
                                        {(() => {
                                            let score = 0;
                                            if (rsi[rsi.length - 1] > 30 && rsi[rsi.length - 1] < 70) score += 1;
                                            if (profile.price > latest50SMA) score += 1;
                                            if (profile.price > latest200SMA) score += 1;
                                            if (macd.macd[macd.macd.length - 1] > macd.signal[macd.signal.length - 1]) score += 1;
                                            if (stochastic.k[stochastic.k.length - 1] > 20 && stochastic.k[stochastic.k.length - 1] < 80) score += 1;
                                            return `${score}/5`;
                                        })()}
                                    </span>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Performance Metrics */}
                    {stockData && trimmedHistoricalData && trimmedHistoricalData.length > 0 && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Performance Metrics</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">1-Day Change</td>
                                            <td className={`text-end ${getPriceChangeColor(profile.change)}`}>
                                                {profile.change > 0 ? '+' : ''}{profile.change?.toFixed(2)} ({profile.changePercentage?.toFixed(2)}%)
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">5-Day Change</td>
                                            <td className={`text-end ${getPriceChangeColor(trimmedHistoricalData.length >= 6 ? 
                                                ((profile.price - trimmedHistoricalData[trimmedHistoricalData.length - 6]?.close) / trimmedHistoricalData[trimmedHistoricalData.length - 6]?.close) * 100 : 0)}`}>
                                                {trimmedHistoricalData.length >= 6 ? 
                                                    (((profile.price - trimmedHistoricalData[trimmedHistoricalData.length - 6]?.close) / trimmedHistoricalData[trimmedHistoricalData.length - 6]?.close) * 100).toFixed(2) + '%' : 
                                                    '-'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">20-Day Change</td>
                                            <td className={`text-end ${getPriceChangeColor(trimmedHistoricalData.length >= 21 ? 
                                                ((profile.price - trimmedHistoricalData[trimmedHistoricalData.length - 21]?.close) / trimmedHistoricalData[trimmedHistoricalData.length - 21]?.close) * 100 : 0)}`}>
                                                {trimmedHistoricalData.length >= 21 ? 
                                                    (((profile.price - trimmedHistoricalData[trimmedHistoricalData.length - 21]?.close) / trimmedHistoricalData[trimmedHistoricalData.length - 21]?.close) * 100).toFixed(2) + '%' : 
                                                    '-'}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">52W High</td>
                                            <td className="text-end">
                                                ${profile.range?.split('-')[1]?.trim() || '-'}
                                                <small className={`d-block ${getPriceChangeColor(profile.price - parseFloat(profile.range?.split('-')[1]?.trim() || 0))}`}>
                                                    {profile.range ? (((profile.price - parseFloat(profile.range.split('-')[1].trim())) / parseFloat(profile.range.split('-')[1].trim())) * 100).toFixed(1) + '% from high' : ''}
                                                </small>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">52W Low</td>
                                            <td className="text-end">
                                                ${profile.range?.split('-')[0]?.trim() || '-'}
                                                <small className={`d-block ${getPriceChangeColor(profile.price - parseFloat(profile.range?.split('-')[0]?.trim() || 0))}`}>
                                                    {profile.range ? (((profile.price - parseFloat(profile.range.split('-')[0].trim())) / parseFloat(profile.range.split('-')[0].trim())) * 100).toFixed(1) + '% from low' : ''}
                                                </small>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Card.Body>
                        </Card>
                    )}


                    {/* Company Info */}
                    {stockData && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Company Info</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <table className="table table-sm table-borderless mb-2">
                                    <tbody>
                                        <tr><td className="text-muted">Industry</td><td className="text-end">{profile.industry}</td></tr>
                                        {profile.ceo && <tr><td className="text-muted">CEO</td><td className="text-end">{profile.ceo}</td></tr>}
                                        {profile.country && <tr><td className="text-muted">Country</td><td className="text-end">{profile.country}</td></tr>}
                                        {profile.fullTimeEmployees && <tr><td className="text-muted">Employees</td><td className="text-end">{formatNumber(profile.fullTimeEmployees)}</td></tr>}
                                        {profile.ipoDate && <tr><td className="text-muted">IPO Date</td><td className="text-end">{profile.ipoDate}</td></tr>}
                                        <tr><td className="text-muted">Dividend</td><td className="text-end">{profile.lastDividend || '-'} {profile.currency}</td></tr>
                                        <tr><td className="text-muted">Website</td><td className="text-end"><a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-truncate d-inline-block" style={{maxWidth: '150px'}}>{profile.website?.replace('https://', '')}</a></td></tr>
                                    </tbody>
                                </table>
                                <Collapse in={showFullDescription}>
                                    <div>
                                        <p className="small text-muted mb-0">{profile.description}</p>
                                    </div>
                                </Collapse>
                                <button
                                    className="btn btn-link btn-sm p-0 text-decoration-none"
                                    onClick={() => setShowFullDescription(!showFullDescription)}
                                >
                                    {showFullDescription ? 'Show less' : 'About company...'}
                                </button>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Ratings Snapshot */}
                    {ratingsSnapshot && Object.keys(ratingsSnapshot).length > 0 && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Ratings Snapshot</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <table className="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr><td className="text-muted">Overall Rating</td><td className="text-end fw-bold">{ratingsSnapshot.rating} ({ratingsSnapshot.overallScore})</td></tr>
                                        <tr><td className="text-muted">DCF Score</td><td className="text-end">{ratingsSnapshot.discountedCashFlowScore}</td></tr>
                                        <tr><td className="text-muted">ROE Score</td><td className="text-end">{ratingsSnapshot.returnOnEquityScore}</td></tr>
                                        <tr><td className="text-muted">ROA Score</td><td className="text-end">{ratingsSnapshot.returnOnAssetsScore}</td></tr>
                                        <tr><td className="text-muted">D/E Score</td><td className="text-end">{ratingsSnapshot.debtToEquityScore}</td></tr>
                                        <tr><td className="text-muted">P/E Score</td><td className="text-end">{ratingsSnapshot.priceToEarningsScore}</td></tr>
                                        <tr><td className="text-muted">P/B Score</td><td className="text-end">{ratingsSnapshot.priceToBookScore}</td></tr>
                                    </tbody>
                                </table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Analyst Consensus (New Card) */}
                    {gradesSummary && !isMutualFund && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom d-flex justify-content-between align-items-center">
                                <strong>Analyst Consensus</strong>
                                <Badge bg={getConsensusColor(gradesSummary.consensus)} className="ms-2">{gradesSummary.consensus}</Badge>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <GradesGaugeChart gradesSummary={gradesSummary} getConsensusColor={getConsensusColor} />
                            </Card.Body>
                        </Card>
                    )}

                    {/* ETF & Fund Country Allocation */}
                    {countryAllocations && countryAllocations.length > 0 && (
                        <Card className="shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Country Allocation</strong>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <CountryAllocationChart data={countryAllocations} />
                                <div className="table-responsive mt-3">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Country</th>
                                                <th className="text-end">Weight</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {countryAllocations.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.country}</td>
                                                    <td className="text-end">{item.weightPercentage}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Latest News */}
                    {newsData && newsData.length > 0 && (
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="py-2 bg-white border-bottom">
                                <strong>Latest News</strong>
                            </Card.Header>
                            <Card.Body className="p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                {newsData.map((news, index) => (
                                    <div key={index} className="p-2 border-bottom">
                                        <a href={news.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                            <small className="fw-semibold text-dark d-block" style={{ lineHeight: '1.3' }}>
                                                {news.title}
                                            </small>
                                        </a>
                                        <small className="text-muted">
                                            {news.site} - {formatToLocalTime(news.publishedDate, DATE_TIME_FORMAT_OPTIONS)}
                                        </small>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default StockDetail;
